/**
 * Identicon 头像组件
 * 基于用户 guid（或钱包地址）生成确定性像素网格头像
 * 风格参考：GitHub Identicon / 以太坊地址头像
 */

import { useMemo } from 'react';

// ============================================================
// 工具函数：简单哈希（djb2 变体）
// ============================================================

/**
 * 将字符串转换为数字哈希
 */
function hashString(str: string): number {
  let hash = 5381;
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) + hash) ^ str.charCodeAt(i);
    hash = hash >>> 0; // 保持 32 位无符号
  }
  return hash;
}

/**
 * 基于哈希生成 HSL 颜色
 * 使用高饱和度、中等亮度确保颜色鲜明
 */
function hashToColor(hash: number): string {
  const hue = hash % 360;
  const saturation = 55 + (hash % 20); // 55-75%
  const lightness = 45 + (hash % 15);  // 45-60%
  return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
}

/**
 * 生成 5x5 像素网格（左右对称，实际只需计算 3 列）
 * 返回 25 个布尔值，true 表示该格子填充颜色
 */
function generateGrid(hash: number): boolean[] {
  const grid: boolean[] = [];
  // 5 行 × 3 列（左半部分），然后镜像到右边
  for (let row = 0; row < 5; row++) {
    for (let col = 0; col < 3; col++) {
      // 用哈希的不同位决定是否填充
      const bitIndex = row * 3 + col;
      const filled = ((hash >> bitIndex) & 1) === 1;
      grid.push(filled);
    }
  }
  // 展开为完整 5×5：[c0, c1, c2, c1, c0] 镜像
  const fullGrid: boolean[] = [];
  for (let row = 0; row < 5; row++) {
    const c0 = grid[row * 3 + 0];
    const c1 = grid[row * 3 + 1];
    const c2 = grid[row * 3 + 2];
    fullGrid.push(c0, c1, c2, c1, c0);
  }
  return fullGrid;
}

// ============================================================
// 组件
// ============================================================

interface IdenticonProps {
  /** 用于生成头像的唯一标识（guid 或钱包地址） */
  guid: string;
  /** 头像尺寸（px），默认 40 */
  size?: number;
  /** 额外 className */
  className?: string;
}

/**
 * Identicon 头像组件
 * 使用 SVG 渲染 5×5 像素网格，左右对称，颜色基于 guid 哈希确定
 */
export function Identicon({ guid, size = 40, className = '' }: IdenticonProps) {
  const { color, bgColor, grid } = useMemo(() => {
    // 使用两个不同哈希种子分别计算颜色和网格
    const h1 = hashString(guid);
    const h2 = hashString(guid + '_grid');

    return {
      color: hashToColor(h1),
      bgColor: `hsl(${h1 % 360}, 15%, 12%)`, // 深色背景
      grid: generateGrid(h2),
    };
  }, [guid]);

  const cellSize = size / 5;
  const padding = size * 0.08; // 8% 内边距

  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      className={className}
      style={{ borderRadius: '50%', display: 'block', flexShrink: 0 }}
      aria-label={`Avatar for ${guid}`}
    >
      {/* 背景 */}
      <rect width={size} height={size} fill={bgColor} />

      {/* 像素网格 */}
      {grid.map((filled, index) => {
        if (!filled) return null;
        const col = index % 5;
        const row = Math.floor(index / 5);
        return (
          <rect
            key={index}
            x={padding + col * cellSize}
            y={padding + row * cellSize}
            width={cellSize - padding * 0.3}
            height={cellSize - padding * 0.3}
            fill={color}
            rx={cellSize * 0.1}
          />
        );
      })}
    </svg>
  );
}

export default Identicon;
